import { Injectable } from '@angular/core';
import { VehicleUtils } from '../utils/vehicle.utils';

export interface VehicleColorScheme {
  background: string;
  color: string;
  border: string;
  countColor: string;
}

export interface VehicleColors {
  vehicleA: VehicleColorScheme;
  vehicleB: VehicleColorScheme;
  [key: string]: VehicleColorScheme;
}

@Injectable({
  providedIn: 'root'
})
export class VehicleColorsService {
  
  private readonly baseColors: VehicleColorScheme[] = [
    { background: '#e3f2fd', color: '#1976d2', border: '#1976d2', countColor: '#1565c0' }, // Blu
    { background: '#fff3e0', color: '#f57c00', border: '#f57c00', countColor: '#ef6c00' }, // Arancione
    { background: '#e8f5e8', color: '#2e7d32', border: '#2e7d32', countColor: '#1b5e20' }, // Verde
    { background: '#fce4ec', color: '#c2185b', border: '#c2185b', countColor: '#ad1457' }, // Rosa
    { background: '#f3e5f5', color: '#7b1fa2', border: '#7b1fa2', countColor: '#6a1b9a' }, // Viola
    { background: '#e0f2f1', color: '#00695c', border: '#00695c', countColor: '#004d40' }, // Teal
    { background: '#fff8e1', color: '#f9a825', border: '#f9a825', countColor: '#f57f17' }, // Giallo
    { background: '#ffebee', color: '#d32f2f', border: '#d32f2f', countColor: '#c62828' }, // Rosso
    { background: '#e8eaf6', color: '#3f51b5', border: '#3f51b5', countColor: '#303f9f' }, // Indigo
    { background: '#e1f5fe', color: '#0288d1', border: '#0288d1', countColor: '#0277bd' }, // Light Blue
    { background: '#e0f7fa', color: '#00acc1', border: '#00acc1', countColor: '#00838f' }, // Cyan
    { background: '#f1f8e9', color: '#689f38', border: '#689f38', countColor: '#558b2f' }, // Light Green
    { background: '#f9fbe7', color: '#9e9d24', border: '#9e9d24', countColor: '#827717' }, // Lime
    { background: '#fffde7', color: '#fbc02d', border: '#fbc02d', countColor: '#f9a825' }, // Yellow
    { background: '#fff3e0', color: '#fb8c00', border: '#fb8c00', countColor: '#f57c00' }, // Orange
    { background: '#fbe9e7', color: '#f4511e', border: '#f4511e', countColor: '#e64a19' }, // Deep Orange
    { background: '#efebe9', color: '#6d4c41', border: '#6d4c41', countColor: '#5d4037' }, // Brown
    { background: '#fafafa', color: '#616161', border: '#616161', countColor: '#424242' }, // Grey
    { background: '#eceff1', color: '#455a64', border: '#455a64', countColor: '#37474f' }, // Blue Grey
    { background: '#e8f5e8', color: '#43a047', border: '#43a047', countColor: '#388e3c' }, // Green Alt
    { background: '#fff9c4', color: '#fdd835', border: '#fdd835', countColor: '#fbc02d' }, // Yellow Alt
    { background: '#ffe0b2', color: '#ff9800', border: '#ff9800', countColor: '#f57c00' }, // Orange Alt
    { background: '#ffcdd2', color: '#e53935', border: '#e53935', countColor: '#d32f2f' }, // Red Alt
    { background: '#f8bbd9', color: '#e91e63', border: '#e91e63', countColor: '#c2185b' }, // Pink Alt
    { background: '#e1bee7', color: '#9c27b0', border: '#9c27b0', countColor: '#7b1fa2' }, // Purple Alt
    { background: '#d1c4e9', color: '#673ab7', border: '#673ab7', countColor: '#512da8' }  // Deep Purple
  ];

  private readonly colors: VehicleColors = {
    vehicleA: this.baseColors[0],
    vehicleB: this.baseColors[1]
  };

  getColors(): VehicleColors {
    return this.colors;
  }

  getVehicleColors(vehicleIndex: number): VehicleColorScheme {
    return this.baseColors[vehicleIndex % this.baseColors.length];
  }

  getVehicleAColors(): VehicleColorScheme {
    return this.colors.vehicleA;
  }

  getVehicleBColors(): VehicleColorScheme {
    return this.colors.vehicleB;
  }

  // Genera colori per N veicoli
  generateColorsForVehicles(vehicleCount: number): { [key: string]: VehicleColorScheme } {
    const result: { [key: string]: VehicleColorScheme } = {};
    const letters = VehicleUtils.getVehicleLetters();
    
    for (let i = 0; i < vehicleCount; i++) {
      const vehicleKey = `vehicle${letters[i] || String.fromCharCode(65 + i)}`;
      result[vehicleKey] = this.getVehicleColors(i);
    }
    
    return result;
  }

  // Metodo per generare CSS custom properties dinamiche
  getCSSVariables(vehicleCount: number = 2): string {
    const colors = this.generateColorsForVehicles(vehicleCount);
    let css = '';
    
    Object.entries(colors).forEach(([key, colorScheme]) => {
      const prefix = key.toLowerCase().replace('vehicle', 'vehicle-');
      css += `
        --${prefix}-bg: ${colorScheme.background};
        --${prefix}-color: ${colorScheme.color};
        --${prefix}-border: ${colorScheme.border};
        --${prefix}-count: ${colorScheme.countColor};`;
    });
    
    return css;
  }

  // Metodo per iniettare CSS variables nel DOM
  injectCSSVariables(vehicleCount: number): void {
    const root = document.documentElement;
    const colors = this.generateColorsForVehicles(vehicleCount);
    
    Object.entries(colors).forEach(([key, colorScheme]) => {
      const prefix = key.toLowerCase().replace('vehicle', 'vehicle-');
      root.style.setProperty(`--${prefix}-bg`, colorScheme.background);
      root.style.setProperty(`--${prefix}-color`, colorScheme.color);
      root.style.setProperty(`--${prefix}-border`, colorScheme.border);
      root.style.setProperty(`--${prefix}-count`, colorScheme.countColor);
    });
  }
}